import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from 'react-bootstrap';
import Sidebar from '../../components/sidebar/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useMask } from '../../utils/privacyMask';
import { getPackageColor, getPackageName, getPackageTitle } from '../../utils/packages';
import './ActivityPage.css';

const TYPE_FILTERS = ['All', 'Subscriptions', 'Transactions', 'Referrals'];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function dayLabel(ts) {
  const d   = new Date(ts);
  const now = new Date();
  const today     = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const day       = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (day === today)     return 'Today';
  if (day === yesterday) return 'Yesterday';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function timeStr(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ActivityPage() {
  const { hidden, mask } = useMask();
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions,  setTransactions]  = useState([]);
  const [referrals,     setReferrals]     = useState([]);
  const [accounts,      setAccounts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [typeFilter,    setTypeFilter]    = useState('All');

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [subRes, txRes, refRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/subscription?all=true`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/transaction`,           { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/referral`,              { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`,               { headers }),
        ]);
        setSubscriptions(subRes.data || []);
        setTransactions(txRes.data   || []);
        setReferrals(refRes.data     || []);
        setAccounts(accRes.data      || []);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch activity');
        setLoading(false);
      }
    })();
  }, []);

  const nameMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a._id] = a.username; });
    return m;
  }, [accounts]);

  /* ── Build unified event list ── */
  const events = useMemo(() => {
    const list = [];

    if (typeFilter === 'All' || typeFilter === 'Subscriptions') {
      subscriptions.forEach(s => {
        const ts = Number(s.createdDate);
        if (!ts) return;
        list.push({
          id:    s._id,
          ts,
          type:  'subscription',
          title: `Subscription — ${mask(nameMap[s.accountID] || s.username || 'Unknown', 'text')}`,
          desc:  `${getPackageTitle(s.package)} – ${getPackageName(s.package)}`,
          value: s.paidPrice ? mask(`€${Number(s.paidPrice).toFixed(2)}`, 'money') : null,
          badge: s.active
            ? { label: 'Active',   bg: 'rgba(34,197,94,0.1)',    color: '#4ade80', border: 'rgba(34,197,94,0.2)'    }
            : { label: 'Inactive', bg: 'rgba(248,113,113,0.1)',  color: '#f87171', border: 'rgba(248,113,113,0.2)'  },
          pkgColor: getPackageColor(s.package),
          icon:  'bi-bookmark-star-fill',
          color: '#f59e0b',
          bg:    'rgba(245,158,11,0.1)',
          link:  `/subscription/view/${s._id}`,
        });
      });
    }

    if (typeFilter === 'All' || typeFilter === 'Transactions') {
      transactions.forEach(t => {
        const ts = Number(t.transactionDate);
        if (!ts) return;
        list.push({
          id:    t._id,
          ts,
          type:  'transaction',
          title: `Transaction — ${mask(nameMap[t.accountID] || 'Unknown', 'text')}`,
          desc:  [t.paymentMethod, t.notes].filter(Boolean).join(' · ') || '—',
          value: t.revenue ? mask(`€${Number(t.revenue).toFixed(2)}`, 'money') : null,
          badge: null,
          icon:  'bi-receipt',
          color: '#3b82f6',
          bg:    'rgba(59,130,246,0.1)',
          link:  `/transaction/view/${t._id}`,
        });
      });
    }

    if (typeFilter === 'All' || typeFilter === 'Referrals') {
      referrals.forEach(r => {
        const ts = Number(r.referDate);
        if (!ts) return;
        list.push({
          id:    r._id,
          ts,
          type:  'referral',
          title: 'Referral',
          desc:  `${mask(nameMap[r.referer] || 'Unknown', 'text')} → ${mask(nameMap[r.refered] || 'Unknown', 'text')}`,
          value: null,
          badge: null,
          icon:  'bi-share-fill',
          color: '#a78bfa',
          bg:    'rgba(167,139,250,0.1)',
          link:  `/referral/view/${r._id}`,
        });
      });
    }

    return list.sort((a, b) => b.ts - a.ts).slice(0, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, transactions, referrals, nameMap, typeFilter, hidden]);

  /* ── Group by day ── */
  const grouped = useMemo(() => {
    const map = new Map();
    events.forEach(e => {
      const key = dayLabel(e.ts);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return Array.from(map.entries());
  }, [events]);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Activity Log" />
      <div className="act-page">

        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        {/* ── Filter tabs ── */}
        <div className="act-filters">
          {TYPE_FILTERS.map(f => (
            <button key={f} className={`act-tab ${typeFilter === f ? 'active' : ''}`} onClick={() => setTypeFilter(f)}>{f}</button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#334155' }}>
            {events.length} event{events.length !== 1 ? 's' : ''}
            {events.length === 300 ? ' (showing latest 300)' : ''}
          </span>
        </div>

        {/* ── Timeline ── */}
        <div className="act-panel">
          <div className="act-panel-header">
            <h6 className="act-panel-title">
              <i className="bi bi-clock-history" style={{ color: '#3b82f6' }} />
              Activity Timeline
            </h6>
          </div>

          {grouped.length === 0 ? (
            <div className="act-empty">
              <i className="bi bi-inbox" />
              No activity found
            </div>
          ) : (
            <div className="act-panel-body">
              {grouped.map(([day, evts]) => (
                <div key={day}>
                  <div className="act-day-label">{day}</div>
                  {evts.map(e => (
                    <div key={`${e.type}-${e.id}`} className="act-event">
                      <div className="act-icon" style={{ background: e.bg, color: e.color }}>
                        <i className={`bi ${e.icon}`} />
                      </div>
                      <div className="act-body">
                        <div className="act-title">{e.title}</div>
                        <div className="act-desc">{e.desc}</div>
                        <div className="act-meta">
                          <span className="act-time">{timeStr(e.ts)}</span>
                          {e.value && (
                            <span className="act-value" style={{ color: e.color }}>{e.value}</span>
                          )}
                          {e.badge && (
                            <span style={{
                              fontSize: '0.65rem', padding: '1px 8px', borderRadius: 20,
                              background: e.badge.bg, color: e.badge.color,
                              border: `1px solid ${e.badge.border}`,
                            }}>
                              {e.badge.label}
                            </span>
                          )}
                          {e.type === 'subscription' && e.pkgColor && (
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.pkgColor, display: 'inline-block' }} />
                          )}
                          <Link to={e.link} className="act-link">View →</Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </>
  );
}
